import { PMDialog } from "../../utils/PMDialog";
import { HourForm } from "./hour_form";
import { useDispatch, useSelector } from "react-redux";
import { deleteHourAsync } from "../../../slices/hour";
import { HourRegistrationWarningDialog } from "./HourRegistrationWarningDialog";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { dispatchWithToastPromise } from "../../../operationFeedback";

export const HourEditDialog = ({
  open,
  hourId,
  onClose,
  onSaved,
  onDeleted,
  prefill,
  hideDescription = false,
  hideAutoSelectedProject = false,
  showPrefilledContext = false,
  panelClassName = "",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const [warningState, setWarningState] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deletePending = useRef(false);

  const handleDelete = async () => {
    if (!hourId || !isAdmin || deletePending.current) return;
    const confirmed = window.confirm("Are you sure you want to delete this hour?");
    if (!confirmed) return;
    deletePending.current = true;
    setDeleting(true);
    let deleted = false;
    try {
      await dispatchWithToastPromise(dispatch(deleteHourAsync(hourId)));
      deleted = true;
    } catch (_) { /* Keep the dialog open when billing protection rejects deletion. */ }
    finally { deletePending.current = false; setDeleting(false); }
    if (deleted) { onDeleted?.(hourId); onClose?.(); }
  };

  return (
    <>
      <PMDialog
        open={open}
        onClose={deleting ? () => {} : onClose}
        size="5xl"
        dense
        panelClassName={`sm:!h-[85vh] sm:!max-h-[85vh] ${panelClassName}`}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 sm:px-0">
            <HourForm
              key={hourId ? String(hourId) : `new-${prefill?.date || "default"}`}
              hourId={hourId}
              onChange={(saved) => {
                if (typeof onSaved === "function") onSaved(saved);
              }}
              prefill={prefill}
              onClose={onClose}
              showQuickHourSelection={!hourId}
              hideDescription={hideDescription}
              hideAutoSelectedProject={hideAutoSelectedProject}
              showPrefilledContext={showPrefilledContext}
              onWarningStateChange={setWarningState}
              renderAfterDescription={() =>
                isAdmin && hourId ? (
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex align-right justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {t("Delete")}
                    </button>
                  </div>
                ) : null
              }
            />
          </div>
        </div>
      </PMDialog>
      
      {warningState && (
        <HourRegistrationWarningDialog
          open={warningState.warningDialogOpen || false}
          onClose={() => {
            if (warningState.setWarningDialogOpen) {
              warningState.setWarningDialogOpen(false);
            }
            if (warningState.setWarningData) {
              warningState.setWarningData(null);
            }
            if (warningState.setPendingSubmission) {
              warningState.setPendingSubmission(null);
            }
          }}
          onConfirm={() => {
            if (warningState.handleWarningConfirm) {
              warningState.handleWarningConfirm();
            }
          }}
          activityTitle={warningState.warningData?.activityTitle}
          estimatedHours={warningState.warningData?.estimatedHours}
          registeredHours={warningState.warningData?.registeredHours}
          newHours={warningState.warningData?.newHours}
          totalAfterRegistration={warningState.warningData?.totalAfterRegistration}
        />
      )}
    </>
  );
};


